import { useCallback, useEffect, useState } from "react";
import {
  listChanges, getChangeDiff, approveChange, rejectChange,
  getInstanceStatus,
  type ChangeSet, type ChangeDiff,
} from "../api";
import { GridLoader, Pill, type Tone } from "./ai/Primitives";
import { IconChanges, IconCheck, IconExternal, IconSpinner } from "./icons";

/** A changed page reads by colour before it reads by word. */
const FILE_TONE: Record<string, Tone> = {
  added: "done", modified: "running", removed: "error", renamed: "accent",
};
const FILE_LABEL: Record<string, string> = {
  added: "Added", modified: "Modified", removed: "Removed", renamed: "Renamed",
};


/**
 * Review gate for an instance's configuration changes: the pull requests a
 * skill edit opens. Shows the per-file diff and lets a human Approve (merge →
 * deploy) or Reject (close).
 *
 * Approve/Reject need a write role (owner/writer) — the session is the
 * credential, enforced by the Worker like every other write action.
 */

export function ReviewPanel({
  open, instance, onClose, onApprovedReload,
}: {
  open: boolean;
  instance: string;
  onClose: () => void;
  onApprovedReload: () => void;
}) {
  const [changes, setChanges] = useState<ChangeSet[]>([]);
  const [diffs, setDiffs] = useState<Record<number, ChangeDiff>>({});
  const [busyPr, setBusyPr] = useState<number | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { changes } = await listChanges(instance);
      setChanges(changes);
      const entries = await Promise.all(
        changes.map(async (c) => {
          try { return [c.pr_number, await getChangeDiff(instance, c.pr_number)] as const; }
          catch { return [c.pr_number, null] as const; }
        }),
      );
      const next: Record<number, ChangeDiff> = {};
      for (const [pr, d] of entries) if (d) next[pr] = d;
      setDiffs(next);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  const pollDeployThenReload = useCallback(async () => {
    setDeploying(true);
    for (let i = 0; i < 30; i++) {
      try {
        const s = await getInstanceStatus(instance);
        if (s.state === "ready" || s.state === "propagating") break;
        if (s.state === "deploy_failed" || s.state === "failed") {
          setErr(`deploy ${s.state}`); setDeploying(false); return;
        }
      } catch { /* keep polling */ }
      await new Promise(r => setTimeout(r, 5000));
    }
    onApprovedReload();
  }, [instance, onApprovedReload]);

  const onApprove = async (pr: number) => {
    setBusyPr(pr); setErr(null);
    try {
      await approveChange(instance, pr);
      await pollDeployThenReload();
    } catch (e) {
      setErr((e as Error).message); setBusyPr(null);
    }
  };

  const onReject = async (pr: number) => {
    setBusyPr(pr); setErr(null);
    try {
      await rejectChange(instance, pr);
      await refresh();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyPr(null);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">Changes awaiting review — <code>{instance}</code></h2>


        {err && <div className="error-banner">{err}</div>}
        {deploying && (
          <div className="provision-state">
            <Pill tone="running" dot>Deploying</Pill>
            <span>Merging.</span>
          </div>
        )}

        {loading && <GridLoader label="Loading change-sets" />}
        {!loading && changes.length === 0 && (
          <div className="empty-state">
            <span className="empty-mark"><IconChanges size={22} /></span>
            <h2>No changes awaiting review.</h2>
          </div>
        )}

        {changes.map((c) => {
          const diff = diffs[c.pr_number];
          const busy = busyPr === c.pr_number || deploying;
          return (
            <div key={c.pr_number} className="review-change">
              <div className="review-change-head">
                <div>
                  <div className="review-change-title">{c.title}</div>
                  <div className="review-change-meta">
                    PR #{c.pr_number} · <code>{c.branch}</code> ·{" "}
                    {diff ? `${diff.files.length} file${diff.files.length === 1 ? "" : "s"} changed`
                          : "loading diff…"} ·{" "}
                    <a href={c.url} target="_blank" rel="noreferrer">
                      GitHub <IconExternal size={11} />
                    </a>
                  </div>
                </div>
                <div className="review-change-actions">
                  <button className="primary" disabled={busy} onClick={() => onApprove(c.pr_number)}>
                    {busy ? <IconSpinner size={14} /> : <IconCheck size={14} />}
                    <span>Approve</span>
                  </button>
                  <button className="danger" disabled={busy} onClick={() => onReject(c.pr_number)}>
                    Reject
                  </button>
                </div>
              </div>

              {diff && diff.files.map((f) => (
                <div key={f.filename} className="review-file">
                  <div className="review-file-head">
                    <Pill tone={FILE_TONE[f.status] ?? "neutral"}>{FILE_LABEL[f.status] ?? f.status}</Pill>
                    <code>{f.page}</code>
                    <span className="review-file-stats">
                      <span className="add">+{f.additions}</span> <span className="del">−{f.deletions}</span>
                    </span>
                  </div>
                  {f.patch ? (
                    <pre className="review-diff">
                      {f.patch.split("\n").map((line, i) => {
                        const cls = line.startsWith("+") && !line.startsWith("+++") ? "diff-add"
                          : line.startsWith("-") && !line.startsWith("---") ? "diff-del"
                          : line.startsWith("@@") ? "diff-hunk" : "";
                        return <div key={i} className={cls}>{line || " "}</div>;
                      })}
                    </pre>
                  ) : (
                    <div className="review-file-nopatch">Binary, or too large to diff inline — open it on GitHub.</div>
                  )}
                </div>
              ))}
            </div>
          );
        })}

        <div className="modal-actions">
          <button onClick={onClose}>Close</button>
          <button onClick={refresh} disabled={loading}>Refresh</button>
        </div>
      </div>
    </div>
  );
}
